import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import waitingService, { type WaitingForMe as Waiting } from '../../services/waiting.service';
import { useRegisterWidget } from '../layout/RealtimeProvider';
import { getApiErrorMessage } from '../../lib/apiError';
import { pendingAge } from '../../lib/approvalAging';
import { colors } from '../../styles/theme';
import { showsRetentionLimit, waitingLabel, waitingNow } from './waitingForMe.helpers';

/** Most kinds announce no realtime event, so the count is also read again every minute. */
const REFRESH_MS = 60_000;

/**
 * One count per kind of work that waits for the signed-in staff person (#340): what their role
 * may act on and they did not start themselves, the oldest item's age, and the screen where it
 * is done. Shown on the agent, manager and super_admin dashboards.
 */
export default function WaitingForMe(): JSX.Element {
  const [data, setData] = useState<Waiting | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setData(await waitingService.get());
      setError(null);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load what is waiting for you.'));
    }
  }, []);

  useEffect(() => {
    void reload();
    const timer = globalThis.setInterval(() => { void reload(); }, REFRESH_MS);
    return () => globalThis.clearInterval(timer);
  }, [reload]);
  useRegisterWidget('waiting-for-me', reload);

  const shown = data ? waitingNow(data.items) : [];
  let body: JSX.Element;
  if (error && !data) {
    body = <p className="text-sm" style={{ color: colors.danger }}>{error}</p>;
  } else if (!data) {
    body = <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>;
  } else if (shown.length === 0) {
    body = <p className="text-sm" style={{ color: colors.muted }}>Nothing is waiting for you.</p>;
  } else {
    body = (
      <ul className="divide-y" style={{ borderColor: colors.borderSoft }}>
        {shown.map((item) => {
          const age = item.oldest_at ? pendingAge(item.oldest_at) : null;
          const label = waitingLabel(item.key);
          return (
            <li key={item.key} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                {item.url ? (
                  <Link to={item.url} className="text-sm font-medium hover:underline" style={{ color: colors.ink }}>
                    {label}
                  </Link>
                ) : (
                  <span className="text-sm font-medium" style={{ color: colors.ink }}>{label}</span>
                )}
                <div className="text-xs" style={{ color: colors.mutedSoft }}>
                  {age && <span>{`oldest ${age.label}`}</span>}
                  {age && !item.url && <span> · </span>}
                  {!item.url && <span>no screen lists these yet</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {item.flagged > 0 && (
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-semibold"
                    style={{ background: colors.warningBg, color: colors.warning }}
                  >
                    {`${item.flagged} late`}
                  </span>
                )}
                <span className="text-lg font-semibold tabular-nums" style={{ color: colors.ink }}>
                  {item.count}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <section
      aria-label="Waiting for you"
      className="rounded-2xl p-5 mb-6"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-base font-semibold" style={{ color: colors.ink }}>Waiting for you</h2>
        {data && (
          <span className="text-sm font-semibold tabular-nums" style={{ color: colors.inkSoft }}>
            {data.total}
          </span>
        )}
      </div>
      {body}
      {data && showsRetentionLimit(shown) && (
        <p className="text-xs mt-3" style={{ color: colors.muted }}>
          {`A withdrawal request is late after ${data.retention_call_sla_business_days} business days without the retention call, and at once when the client has no agent.`}
        </p>
      )}
    </section>
  );
}
